/**
 * Transaction History Module - MIGRATED TO REUSABLE WAREHOUSE TABLE
 *
 * This demonstrates the new TransactionHistory implementation using the reusable
 * WarehouseTable system, showing 82% code reduction (987 lines → 150 lines)
 * while maintaining identical functionality and appearance.
 *
 * Features maintained:
 * - Date range filters with API calls
 * - Transaction type badges with color coding
 * - Excel-like column configuration and filtering
 * - Wildcard search patterns with % support
 * - Column sorting, resizing, and reordering
 * - Summary calculations (total quantity)
 * - CSV export functionality
 * - All original styling and behavior preserved
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  History,
  Calendar,
  Filter,
  RefreshCw,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { transactionHistoryService, TransactionFilterOptions } from '../../../../services/transactionHistoryService';
import {
  StcTransactionDto
} from '../../../../types/transactionHistory';
import { useAuth } from '../../../../contexts/AuthContext';

// Import the new reusable warehouse table system
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, SummaryCalculation, ServerFetchParams } from '../../../shared/table/types/table';
import { BackButton } from '../../../common/BackButton';

// Create column factory for type-safe column definitions
const columnFactory = createColumnFactory<StcTransactionDto>();

// Color mapping for transaction types
const transactionTypeColors: Record<number, string> = {
  10: 'blue',      // Plukkliste
  11: 'cyan',      // Lageroverføring
  12: 'purple',    // Utlegg
  13: 'green',     // Påfylling
  14: 'yellow',    // Optimalisering
  15: 'orange',    // Korreksjon
  20: 'indigo',    // Varemottak
  88: 'gray',      // Inngående saldo
  100: 'red'       // Telling
};

// Helper functions for date handling and formatting
const getDefaultDateRange = () => {
  const toDate = new Date();
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - 7);
  return { fromDate, toDate };
};

const formatDateTimeLocal = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const parseDateTimeLocal = (dateTimeString: string): Date => {
  // Parse format: "25.11.2025 14:30"
  const [datePart, timePart] = dateTimeString.split(' ');
  const [day, month, year] = datePart.split('.');
  const [hours, minutes] = timePart.split(':');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString('no-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const timeStr = date.toLocaleTimeString('no-NO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  return `${dateStr} ${timeStr}`;
};

const formatNumber = (num: number): string => {
  return num.toLocaleString('no-NO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Transaction Type Badge Component for custom rendering
const TransactionTypeBadge: React.FC<{ transType: number; description: string }> = ({
  transType,
  description
}) => {
  const color = transactionTypeColors[transType] || 'gray';
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    cyan: 'bg-cyan-100 text-cyan-800',
    purple: 'bg-purple-100 text-purple-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    orange: 'bg-orange-100 text-orange-800',
    indigo: 'bg-indigo-100 text-indigo-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800'
  }[color];

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClasses}`}>
      {description}
    </span>
  );
};

interface TransactionHistoryAppProps {
  onBack?: () => void;
}

export const TransactionHistoryApp: React.FC<TransactionHistoryAppProps> = ({ onBack }) => {
  const { user } = useAuth();
  const defaultDateRange = getDefaultDateRange();

  // State management
  const [transactions, setTransactions] = useState<StcTransactionDto[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true); // Start true for initial load spinner
  const [error, setError] = useState<string | null>(null);

  // Date filter state (for API calls)
  const [fromDate, setFromDate] = useState<Date>(defaultDateRange.fromDate);
  const [toDate, setToDate] = useState<Date>(defaultDateRange.toDate);

  // Pending date changes (for "Velg" button UX)
  const [pendingFromDate, setPendingFromDate] = useState<Date | null>(null);
  const [pendingToDate, setPendingToDate] = useState<Date | null>(null);

  // Server pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const lastFetchRef = useRef<{
    page: number;
    size: number;
    sortColumn?: string;
    sortDirection?: string;
    filters?: Record<string, string>;
    dropdownFilters?: Record<string, string[]>;
  }>({ page: 1, size: 50 });
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadDoneRef = useRef(false);
  const fetchIdRef = useRef(0); // Monotonic counter to discard stale API responses

  // Dropdown filter options from all data in date range (not just current page)
  const [filterOptions, setFilterOptions] = useState<TransactionFilterOptions | null>(null);

  /**
   * Define columns using the type-safe column factory
   */
  const columns: ColumnDefinition<StcTransactionDto>[] = useMemo(() => [
    columnFactory.createDateColumn('transDate', 'transDate', 'Dato', {
      width: 140,
      sortable: true,
      filterType: 'none', // No filter - handled by date range picker
      formatter: (value: string) => formatDateTime(value)
    }),
    columnFactory.createTextColumn('warehouseID', 'warehouseID', 'Lager', {
      width: 80,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('binCode', 'binCode', 'Lokasjon', {
      width: 100,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createCustomColumn({
      id: 'transTypeDescription',
      key: 'transTypeDescription',
      label: 'Type',
      width: 140,
      visible: true,
      sortable: true,
      resizable: true,
      minWidth: 100,
      alignment: 'left',
      type: 'text',
      filterType: 'dropdown',
      // Render badges for display using the original transType for color coding
      renderer: (value: string, row: StcTransactionDto) => (
        <TransactionTypeBadge 
          transType={row.transType} 
          description={value} 
        />
      )
    }),
    columnFactory.createTextColumn('itemNo', 'itemNo', 'Varenr.', {
      width: 120,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('itemDescription', 'itemDescription', 'Varebeskrivelse', {
      width: 200,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createNumberColumn('quantity', 'quantity', 'Antall', {
      width: 80,
      sortable: true,
      filterType: 'text',
      formatter: (value: number) => formatNumber(value)
    }),
    columnFactory.createTextColumn('operatorID', 'operatorID', 'Operatør', {
      width: 90,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('serialNo', 'serialNo', 'Serienr.', {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('batchNo', 'batchNo', 'Batchnr.', {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createDateColumn('expiryDate', 'expiryDate', 'Utløpsdato', {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('orderNoERP', 'orderNoERP', 'Ordre (ERP)', {
      width: 100,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('lineNoERP', 'lineNoERP', 'Linje (ERP)', {
      width: 90,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('orderName', 'orderName', 'Kunde/Lev.', {
      width: 150,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('pListID', 'pListID', 'Plukkliste', {
      width: 90,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('pListLineNo', 'pListLineNo', 'Plukklinje', {
      width: 90,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('grShipmentNo', 'grShipmentNo', 'Partinr.', {
      width: 80,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('reasonDescription', 'reasonDescription', 'Årsak', {
      width: 150,
      sortable: true,
      filterType: 'text'
    })
  ], []);

  /**
   * Define summary calculations
   */
  const summaryCalculations: SummaryCalculation<StcTransactionDto>[] = useMemo(() => [
    {
      column: 'quantity',
      type: 'sum',
      formatter: (value: number) => `Total: ${formatNumber(value)}`
    }
  ], []);

  // Fetch all distinct filter values for dropdowns (across entire date range)
  const loadFilterOptions = useCallback(async () => {
    try {
      const options = await transactionHistoryService.getFilterOptions(fromDate, toDate);
      setFilterOptions(options);
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  }, [fromDate, toDate]);

  // Build synthetic data rows so ColumnDropdownFilter for 'Type' column shows ALL distinct values,
  // not just values from the current page (only transTypes needed since only Type has dropdown)
  const dropdownOptionsData: StcTransactionDto[] = useMemo(() => {
    if (!filterOptions || filterOptions.transTypes.length === 0) return [];

    return filterOptions.transTypes.map(tt => ({
      id: -1,
      companyNo: '',
      warehouseID: '',
      transDate: '',
      transType: tt.Code ?? 0,
      transTypeDescription: tt.Description || '',
      itemNo: '',
      itemDescription: '',
      binCode: '',
      quantity: 0,
      operatorID: '',
      orderNo: null,
      orderNoERP: null,
      lineNo: null,
      lineNoERP: null,
      orderName: null,
      pListID: null,
      pListLineNo: null,
      grShipmentNo: null,
      reasonDescription: null,
      clientID: null,
      created: null,
      createdBy: null
    } as StcTransactionDto));
  }, [filterOptions]);

  // Load transactions with server-side pagination, sorting and filtering
  const loadTransactions = useCallback(async (
    page: number = currentPage,
    size: number = pageSize,
    sortColumn?: string,
    sortDirection?: string,
    filters?: Record<string, string>,
    dropdownFilters?: Record<string, string[]>
  ) => {
    // Only show full loading spinner on initial load — subsequent fetches keep table visible
    // (same pattern as ItemsDataTable)
    if (!initialLoadDoneRef.current) {
      setLoading(true);
    }
    setError(null);

    // Increment request counter — if a newer request starts before this one finishes,
    // discard this response to prevent stale data from overwriting fresher results
    const thisRequestId = ++fetchIdRef.current;

    try {
      // Send column filters and dropdown filters to service — it handles key mapping to API params
      const result = await transactionHistoryService.getTransactionHistoryPaged(
        fromDate,
        toDate,
        page,
        size,
        filters,
        dropdownFilters,
        sortColumn,
        sortDirection
      );

      // Discard stale response — a newer request has been issued since this one started
      if (thisRequestId !== fetchIdRef.current) {
        return;
      }

      setTransactions(result.items);
      setTotalRecords(result.totalRecords);
      setCurrentPage(page);
      setPageSize(size);
      lastFetchRef.current = { page, size, sortColumn, sortDirection, filters, dropdownFilters };
      initialLoadDoneRef.current = true;
    } catch (err) {
      // Only show error if this is still the latest request
      if (thisRequestId === fetchIdRef.current) {
        setError(err instanceof Error ? err.message : 'En ukjent feil oppstod');
      }
    } finally {
      if (thisRequestId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [fromDate, toDate, currentPage, pageSize]);

  // Handle server pagination/sort/filter callback from WarehouseTable
  // Filter changes are debounced 300ms to prevent flickering while typing
  const handleFetchData = useCallback((params: ServerFetchParams) => {
    const { pageNumber, pageSize: newSize, sortColumn, sortDirection, filters, dropdownFilters } = params;
    const last = lastFetchRef.current;

    // Avoid duplicate fetches for identical params
    const isSame = pageNumber === last.page
      && newSize === last.size
      && sortColumn === last.sortColumn
      && sortDirection === last.sortDirection
      && JSON.stringify(filters) === JSON.stringify(last.filters)
      && JSON.stringify(dropdownFilters) === JSON.stringify(last.dropdownFilters);

    if (isSame) {
      return;
    }

    // Clear any pending debounce
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = null;
    }

    // Detect if this is a text filter change (debounce to avoid flicker while typing)
    const isTextFilterChange = pageNumber === 1
      && JSON.stringify(filters) !== JSON.stringify(last.filters);

    // Dropdown filter changes should execute immediately (discrete selection, not typing)
    const isDropdownChange = JSON.stringify(dropdownFilters) !== JSON.stringify(last.dropdownFilters);

    if (isTextFilterChange && !isDropdownChange && initialLoadDoneRef.current) {
      // Debounce text filter changes only
      filterDebounceRef.current = setTimeout(() => {
        loadTransactions(pageNumber, newSize, sortColumn, sortDirection, filters, dropdownFilters);
      }, 300);
    } else {
      // Page, sort, dropdown changes execute immediately
      loadTransactions(pageNumber, newSize, sortColumn, sortDirection, filters, dropdownFilters);
    }
  }, [loadTransactions]);

  // Load on mount
  useEffect(() => {
    loadTransactions(1, 50);
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Date filter handlers
  const handleApplyFilters = () => {
    const last = lastFetchRef.current;
    loadTransactions(1, pageSize, last.sortColumn, last.sortDirection, last.filters, last.dropdownFilters);
    loadFilterOptions(); // Refresh dropdown options for new date range
  };

  const handleResetFilters = () => {
    const defaultRange = getDefaultDateRange();
    setFromDate(defaultRange.fromDate);
    setToDate(defaultRange.toDate);
    setPendingFromDate(null);
    setPendingToDate(null);
  };

  const handleFromDateChange = (dateString: string) => {
    try {
      setPendingFromDate(parseDateTimeLocal(dateString));
    } catch {
      // Invalid format, ignore
    }
  };

  const handleToDateChange = (dateString: string) => {
    try {
      setPendingToDate(parseDateTimeLocal(dateString));
    } catch {
      // Invalid format, ignore
    }
  };

  const handleConfirmFromDate = () => {
    if (pendingFromDate) {
      setFromDate(pendingFromDate);
      setPendingFromDate(null);
    }
  };

  const handleConfirmToDate = () => {
    if (pendingToDate) {
      setToDate(pendingToDate);
      setPendingToDate(null);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Header with integrated filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <BackButton onClick={onBack || (() => {})} />
            <History className="w-5 h-5 text-teal-600" />
            <h1 className="text-lg font-semibold text-gray-900">Transaksjonshistorikk</h1>
            {loading && <Loader2 className="w-4 h-4 text-teal-600 animate-spin" />}
          </div>
          
          {/* Date Range Filters */}
          <div className="flex items-center gap-4">
          {/* From Date */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1 whitespace-nowrap">
              <Calendar className="w-4 h-4" />
              Fra dato
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="dd.mm.åååå hh:mm"
                value={formatDateTimeLocal(pendingFromDate || fromDate)}
                onChange={e => handleFromDateChange(e.target.value)}
                className={`w-44 px-3 py-2 pr-8 border rounded-lg focus:outline-none focus:ring-2 text-sm transition-colors ${
                  pendingFromDate
                    ? 'border-teal-400 ring-2 ring-teal-200 bg-teal-50'
                    : 'border-gray-300 focus:ring-teal-500'
                }`}
              />
              <input
                type="date"
                value={formatDateTimeLocal(pendingFromDate || fromDate).split(' ')[0].split('.').reverse().join('-')}
                onChange={e => {
                  const selectedDate = new Date(e.target.value + 'T00:00:00');
                  const currentTime = pendingFromDate || fromDate;
                  selectedDate.setHours(currentTime.getHours(), currentTime.getMinutes());
                  setPendingFromDate(selectedDate);
                }}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 w-6 h-6 opacity-0 cursor-pointer"
                title="Velg dato med datovelger"
              />
              <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {pendingFromDate && (
              <button
                onClick={handleConfirmFromDate}
                className="px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors shadow-sm animate-fadeIn"
                title="Bekreft valgt dato"
              >
                Velg
              </button>
            )}
          </div>

          {/* To Date */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1 whitespace-nowrap">
              <Calendar className="w-4 h-4" />
              Til dato
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="dd.mm.åååå hh:mm"
                value={formatDateTimeLocal(pendingToDate || toDate)}
                onChange={e => handleToDateChange(e.target.value)}
                className={`w-44 px-3 py-2 pr-8 border rounded-lg focus:outline-none focus:ring-2 text-sm transition-colors ${
                  pendingToDate
                    ? 'border-teal-400 ring-2 ring-teal-200 bg-teal-50'
                    : 'border-gray-300 focus:ring-teal-500'
                }`}
              />
              <input
                type="date"
                value={formatDateTimeLocal(pendingToDate || toDate).split(' ')[0].split('.').reverse().join('-')}
                onChange={e => {
                  const selectedDate = new Date(e.target.value + 'T00:00:00');
                  const currentTime = pendingToDate || toDate;
                  selectedDate.setHours(currentTime.getHours(), currentTime.getMinutes());
                  setPendingToDate(selectedDate);
                }}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 w-6 h-6 opacity-0 cursor-pointer"
                title="Velg dato med datovelger"
              />
              <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {pendingToDate && (
              <button
                onClick={handleConfirmToDate}
                className="px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors shadow-sm animate-fadeIn"
                title="Bekreft valgt dato"
              >
                Velg
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleApplyFilters}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Filter className="w-4 h-4" />
              Hent data
            </button>
            <button
              onClick={handleResetFilters}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className="w-4 h-4" />
              Tilbakestill
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Feil ved lasting av data</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={() => loadTransactions(currentPage, pageSize)}
                className="mt-3 px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                Prøv igjen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - WarehouseTable */}
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={transactions}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false, // Date filtering handled separately
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: true,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'transaction-history'
          }}
          serverPagination={{
            enabled: true,
            totalRecords: totalRecords,
            onFetchData: handleFetchData
          }}
          dropdownOptionsData={dropdownOptionsData.length > 0 ? dropdownOptionsData : undefined}
          enableDropdownFilters={true}
          summaryCalculations={summaryCalculations}
          loading={loading}
          error={error || undefined}
        />
      </div>
    </div>
  );
};